/**
 * company_info.repository.ts — 회사 기본 정보 (**법인별**, 12_통합관리_계정기기설계 §9-1).
 *
 * Firestore:
 *   - 법인 문서 `tenants/{t}/settings/company_info` — 사업자등록증 기준 회사 정보·주소(출발지)·연락처.
 *     견적서·계약서·일보 등 문서 출력의 공급자 정보. 편집 = admin·그 법인 관리자(rules §5-6).
 *   - 루트 문서 `system_settings/company_info` — **브랜드**(로고·그룹명). 로그인 화면·사이드바가 읽는다(공개 읽기).
 *     키 이름은 그대로 두었다(§9-1 ③ 추천안 — 변경이 가장 적음).
 *
 * 전환 중 대체 읽기(복사 스크립트 copy_company_info_to_tenant.js 실행 전 무중단):
 *   - 중앙: 법인 문서가 없거나 읽기가 거부되면(rules 배포 전·소속 문서 없는 옛 계정) 루트 값을 대신 보여 준다(source "root").
 *   - 경원: 법인 문서가 없으면 빈 값(source "empty") — 화면이 "아직 입력 전" 안내.
 *
 * 필드명은 기존 영업 도메인(domain/sales.ts: businessNo/businessType/businessItem)과 맞춤.
 */
import { doc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { BRAND_SETTINGS_DOC_PATH, settingsDocPath, type SettingsAt } from "@/domain/tenantPath";
import type { Coord } from "./tmap";
import { EMPTY_LOGO_SLOTS, parseLogoSlots, type LogoSlots } from "@/domain/brand_logo";

const KEY = "company_info";

/** 화면에 보이는 값이 어디서 왔나 — tenant = 법인 문서, root = 루트(브랜드) 문서로 대체, empty = 아직 없음 */
export type CompanyInfoSource = "tenant" | "root" | "empty";

/** 업태/종목 항목. primary=대표(문서 출력 시 우선 표기). */
export interface CompanyTag {
  value: string;
  primary: boolean;
}

export interface CompanyInfo {
  // 로고 (data URL — SVG/PNG/JPG). 옛 단일 칸 = logos.full_light 와 같은 값(호환용).
  logo: string;
  // 로고 4칸(심볼·전체형 × 라이트·다크). 고르기는 domain/brand_logo.ts pickLogo.
  logos: LogoSlots;
  // 기본
  companyName: string; // 상호 / 법인명
  representative: string; // 대표자
  businessNo: string; // 사업자등록번호 (3-2-5)
  // 등록 · 분류
  corporateNo: string; // 법인등록번호 (6-7)
  businessTypes: CompanyTag[]; // 업태 목록 (대표 1건)
  businessItems: CompanyTag[]; // 종목 목록 (대표 1건)
  // 연락처
  phone: string; // 대표 전화
  fax: string; // 팩스
  email: string; // 이메일
  website: string; // 홈페이지
  // 주소
  postalCode: string; // 우편번호
  address1: string; // 기본 주소 — 좌표·거리 계산의 기준이 되는 줄
  address2: string; // 상세 주소 (동·층·호 등, 좌표와 무관)
  /**
   * address1 의 확정 좌표. 통합 위치 지정(LocationPicker)에서 함께 저장한다.
   * 있으면 화면들이 주소를 매번 지오코딩하지 않고 이 값을 바로 쓴다.
   */
  coord?: Coord | null;
  /**
   * 이 주소를 거리·경로 계산의 **출발지**로 쓸지 여부.
   *
   * 왜 필요한가 — 우리 회사는 본사·공장·견적 출발지가 모두 같은 한 곳(군서로)인데
   * 설정이 세 벌(company_info / plant_info / quote.settings.route_start_address)로
   * 흩어져 있었다. 여기를 켜면 이 주소 하나가 전 화면의 출발지가 된다.
   * 자세한 우선순위는 hooks/useRouteOrigin.ts.
   */
  is_route_origin: boolean;
}

export const DEFAULT_COMPANY_INFO: CompanyInfo = {
  logo: "",
  logos: EMPTY_LOGO_SLOTS,
  companyName: "",
  representative: "",
  businessNo: "",
  corporateNo: "",
  businessTypes: [],
  businessItems: [],
  phone: "",
  fax: "",
  email: "",
  website: "",
  postalCode: "",
  address1: "",
  address2: "",
  coord: null,
  is_route_origin: false,
};

const str = (v: unknown): string => (typeof v === "string" ? v : "");

function toCoord(v: unknown): Coord | null {
  const c = v as { lat?: unknown; lng?: unknown } | null | undefined;
  const lat = Number(c?.lat);
  const lng = Number(c?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function parseTags(v: unknown): CompanyTag[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => {
    const o = (x ?? {}) as Record<string, unknown>;
    return { value: str(o.value), primary: o.primary === true };
  });
}

function fromDoc(data: Record<string, unknown> | undefined): CompanyInfo {
  if (!data) return { ...DEFAULT_COMPANY_INFO };
  return {
    logo: str(data.logo),
    logos: parseLogoSlots(data.logos, data.logo),
    companyName: str(data.companyName),
    representative: str(data.representative),
    businessNo: str(data.businessNo),
    corporateNo: str(data.corporateNo),
    businessTypes: parseTags(data.businessTypes),
    businessItems: parseTags(data.businessItems),
    phone: str(data.phone),
    fax: str(data.fax),
    email: str(data.email),
    website: str(data.website),
    postalCode: str(data.postalCode),
    address1: str(data.address1),
    address2: str(data.address2),
    coord: toCoord(data.coord),
    is_route_origin: data.is_route_origin === true,
  };
}

export const companyInfoRepo = {
  /**
   * 법인 회사정보 구독. 중앙은 법인 문서가 없거나 거부되면 루트 문서로 대체(위 머리말).
   * cb 두 번째 인자 = 값의 출처.
   */
  watch(
    at: SettingsAt,
    cb: (info: CompanyInfo, source: CompanyInfoSource) => void,
    onError?: (e: Error) => void,
  ): () => void {
    const tenantRef = doc(db, settingsDocPath(KEY, at));
    const canFallback = at.tenant === "joongang";
    let usingTenant = false;
    let unsubRoot: (() => void) | null = null;
    let closed = false;

    const startRoot = () => {
      if (unsubRoot || closed) return;
      unsubRoot = onSnapshot(
        doc(db, BRAND_SETTINGS_DOC_PATH),
        (rs) => {
          if (usingTenant) return;
          if (rs.exists()) cb(fromDoc(rs.data() as Record<string, unknown>), "root");
          else cb({ ...DEFAULT_COMPANY_INFO }, "empty");
        },
        onError,
      );
    };
    const stopRoot = () => {
      unsubRoot?.();
      unsubRoot = null;
    };

    const unsubTenant = onSnapshot(
      tenantRef,
      (snap) => {
        if (snap.exists()) {
          usingTenant = true;
          stopRoot();
          cb(fromDoc(snap.data() as Record<string, unknown>), "tenant");
          return;
        }
        usingTenant = false;
        if (canFallback) startRoot();
        else cb({ ...DEFAULT_COMPANY_INFO }, "empty");
      },
      (e) => {
        // rules 배포 전(블록 없음)·소속 문서 없는 옛 중앙 계정은 permission-denied → 중앙은 루트로 대체
        usingTenant = false;
        if (canFallback) startRoot();
        else onError?.(e);
      },
    );
    return () => {
      closed = true;
      unsubTenant();
      stopRoot();
    };
  },

  /** 법인 회사정보 저장 — 법인 문서에만 쓴다(루트 브랜드 문서는 건드리지 않음) */
  async save(at: SettingsAt, info: CompanyInfo): Promise<void> {
    await setDoc(
      doc(db, settingsDocPath(KEY, at)),
      {
        ...info,
        // undefined 는 Firestore 가 거부한다 — 좌표 미확정은 null 로 명시해 지운다
        coord: info.coord ?? null,
        updated_at: Timestamp.now(),
      },
      { merge: true },
    );
  },

  /** 루트 브랜드(로고·그룹명) 구독 — 로그인 화면·사이드바. 문서 형식은 옛 company_info 그대로 */
  watchBrand(cb: (info: CompanyInfo) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      doc(db, BRAND_SETTINGS_DOC_PATH),
      (snap) => cb(fromDoc(snap.exists() ? (snap.data() as Record<string, unknown>) : undefined)),
      onError,
    );
  },

  /** 루트 브랜드 저장(로고 4칸·그룹명만) — 통합관리(전체 admin) 화면용. 법인 환경설정은 쓰지 않는다.
   *  옛 `logo` 칸에는 full_light 를 같이 써서 옛 번들·스크립트가 계속 읽게 한다 */
  async saveBrand(brand: Pick<CompanyInfo, "logos" | "companyName">): Promise<void> {
    await setDoc(
      doc(db, BRAND_SETTINGS_DOC_PATH),
      { logo: brand.logos.full_light, logos: { ...brand.logos }, companyName: brand.companyName, updated_at: Timestamp.now() },
      { merge: true },
    );
  },
};
